import React, { useState, useEffect, useCallback } from 'react';
import {
  StyleSheet,
  Text,
  View,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Image,
  RefreshControl,
  ActivityIndicator,
  Platform,
  StatusBar,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Haptics from 'expo-haptics';
import { useTranslation } from 'react-i18next';
// NOTE : VueProfil reste dans (tabs)/ à côté de ce fichier, donc l'import
// relatif './VueProfil' ne change pas (pas de niveau à corriger ici,
// contrairement aux imports vers components/ ou lib/ à la racine).
import VueProfil from './VueProfil';

// ── Hook auto-hide (tab bar globale synchronisée au scroll) ─────────────────
// Cet écran n'a pas de header local animé, donc on branche le hook sans
// onShow/onHide — seule la tab bar globale du (tabs)/_layout.tsx est pilotée.
import { useScrollAutoHide } from '../../hooks/useScrollAutoHide';

// ─── Config ───────────────────────────────────────────────────────────────────
const API_BASE = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com/friends';

const COLORS = {
  primary:      '#7B1FA2',
  primaryLight: '#9C27B0',
  primaryDark:  '#4A0072',
  accent:       '#E040FB',
  success:      '#00C853',
  error:        '#FF1744',
  warning:      '#FFD600',
  bg:           '#F3E5F5',
  card:         '#FFFFFF',
  border:       '#E1BEE7',
  textPrimary:  '#1A0033',
  textSecondary:'#6D4C7D',
  textMuted:    '#AB8FBB',
  badge:        '#FF1744',
};

// ─── Types ────────────────────────────────────────────────────────────────────
type TabName = 'suggestions' | 'search' | 'requests';
type RequestTabName = 'received' | 'sent';

interface User {
  id: string;
  nom: string;
  prenom: string;
  avatar_url: string | null;
  created_at: string;
}

interface FriendRequest {
  id: string;
  requester_id: string;
  receiver_id: string;
  status: string;
  created_at: string;
  requester: User;
  receiver: User;
}

// ─── Métadonnées des onglets principaux (icônes, structure — non traduisibles) ─
interface TabMeta {
  key: TabName;
  icon: string;
}

const TABS_META: TabMeta[] = [
  { key: 'suggestions', icon: 'sparkles' },
  { key: 'search',      icon: 'search'   },
  { key: 'requests',    icon: 'mail'     },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (nom: string, prenom: string) =>
  `${(prenom ?? '').charAt(0)}${(nom ?? '').charAt(0)}`.toUpperCase();

const haptic = (type: 'light' | 'medium' | 'success') => {
  if (Platform.OS === 'web') return;
  if (type === 'success') Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
  else if (type === 'medium') Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
  else Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
};

// ─── API call ─────────────────────────────────────────────────────────────────
async function apiFriends(body: Record<string, any>) {
  const session = await AsyncStorage.getItem('harmonia_session');
  if (!session) throw new Error('Session introuvable — reconnectez-vous');
  const { access_token } = JSON.parse(session);

  const res = await fetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, access_token }),
  });
  return res.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function Avatar({ user, size = 50 }: { user: User; size?: number }) {
  return user.avatar_url ? (
    <Image
      source={{ uri: user.avatar_url }}
      style={{ width: size, height: size, borderRadius: size / 2 }}
    />
  ) : (
    <LinearGradient
      colors={[COLORS.primaryLight, COLORS.primaryDark]}
      style={{
        width: size, height: size, borderRadius: size / 2,
        justifyContent: 'center', alignItems: 'center',
      }}
    >
      <Text style={{ color: '#fff', fontSize: size * 0.35, fontWeight: '700' }}>
        {getInitials(user.nom, user.prenom)}
      </Text>
    </LinearGradient>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────────────────────
export default function FriendsScreen() {
  const { t }                                 = useTranslation();
  const [activeTab, setActiveTab]             = useState<TabName>('suggestions');
  const [requestTab, setRequestTab]           = useState<RequestTabName>('received');
  const [refreshing, setRefreshing]           = useState(false);
  const [loading, setLoading]                 = useState(false);
  const [loadingMore, setLoadingMore]         = useState(false);
  const [searchQuery, setSearchQuery]         = useState('');
  const [selectedUserId, setSelectedUserId]   = useState<string | null>(null);
  const [pendingActions, setPendingActions]   = useState<Set<string>>(new Set());

  // ── Tab bar auto-hide au scroll ──────────────────────────────────────────
  const { handleScroll, handleDoubleTap } = useScrollAutoHide();

  // Suggestions
  const [suggestions, setSuggestions]               = useState<User[]>([]);
  const [suggestionsOffset, setSuggestionsOffset]   = useState(0);
  const [hasMoreSuggestions, setHasMoreSuggestions] = useState(true);

  // Search
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [searching, setSearching]         = useState(false);

  // Requests
  const [receivedRequests, setReceivedRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests]         = useState<FriendRequest[]>([]);

  useEffect(() => { loadTab(); }, [activeTab]);

  const loadTab = async () => {
    setLoading(true);
    try {
      if (activeTab === 'suggestions') await loadSuggestions(0);
      else if (activeTab === 'requests') await loadRequests();
    } finally {
      setLoading(false);
    }
  };

  // ── Suggestions ─────────────────────────────────────────────────────────────
  const loadSuggestions = async (offset: number) => {
    try {
      const data = await apiFriends({ action: 'get-suggestions', offset, limit: 20 });
      if (data.success && data.users) {
        if (offset === 0) setSuggestions(data.users);
        else setSuggestions(prev => [...prev, ...data.users]);
        setSuggestionsOffset(offset);
        setHasMoreSuggestions(data.users.length === 20 && offset + 20 < 100);
      }
    } catch (e) { console.error('[loadSuggestions]', e); }
  };

  const loadMoreSuggestions = async () => {
    if (!hasMoreSuggestions || loadingMore) return;
    setLoadingMore(true);
    await loadSuggestions(suggestionsOffset + 20);
    setLoadingMore(false);
  };

  // ── Search ──────────────────────────────────────────────────────────────────
  const searchUsers = async () => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const data = await apiFriends({ action: 'search-users', query: searchQuery.trim() });
      if (data.success) setSearchResults(data.users ?? []);
    } catch (e) { console.error('[searchUsers]', e); }
    finally { setSearching(false); }
  };

  // ── Requests ────────────────────────────────────────────────────────────────
  const loadRequests = async () => {
    try {
      const data = await apiFriends({ action: 'get-requests' });
      if (data.success) {
        setReceivedRequests(data.received ?? []);
        setSentRequests(data.sent ?? []);
      }
    } catch (e) { console.error('[loadRequests]', e); }
  };

  // ── Actions ──────────────────────────────────────────────────────────────────
  const withPending = (id: string, fn: () => Promise<void>) => async () => {
    if (pendingActions.has(id)) return;
    setPendingActions(prev => new Set(prev).add(id));
    try { await fn(); }
    finally {
      setPendingActions(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const sendRequest = (targetId: string) => withPending(targetId, async () => {
    haptic('medium');
    const data = await apiFriends({ action: 'send-request', target_user_id: targetId });
    if (data.success) {
      setSuggestions(prev => prev.filter(u => u.id !== targetId));
      setSearchResults(prev => prev.filter(u => u.id !== targetId));
      if (activeTab === 'search') {
        await loadRequests(); 
      }
    }
  })();

  const acceptRequest = (requestId: string) => withPending(requestId, async () => {
    haptic('success');
    const data = await apiFriends({ action: 'accept-request', request_id: requestId });
    if (data.success) await loadRequests();
  })();

  const cancelRequest = (requestId: string) => withPending(requestId, async () => {
    haptic('light');
    const data = await apiFriends({ action: 'cancel-request', request_id: requestId });
    if (data.success) await loadRequests();
  })();

  const onRefresh = async () => {
    setRefreshing(true);
    await loadTab();
    setRefreshing(false);
  };

  const totalRequests = receivedRequests.length + sentRequests.length;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDERS
  // ─────────────────────────────────────────────────────────────────────────

  const renderUserCard = (user: User) => {
    const isPending = pendingActions.has(user.id);
    return (
      <TouchableOpacity
        key={user.id}
        style={styles.card}
        onPress={() => setSelectedUserId(user.id)}
        activeOpacity={0.92}
      >
        <View style={styles.cardLeft}>
          <View style={styles.avatarWrap}>
            <Avatar user={user} size={52} />
          </View>
          <View style={styles.cardInfo}>
            <Text style={styles.cardName} numberOfLines={1}>
              {user.prenom} {user.nom}
            </Text>
          </View>
        </View>
        <TouchableOpacity
          style={[styles.iconBtn, styles.btnPrimary, isPending && styles.btnDisabled]}
          onPress={() => sendRequest(user.id)}
          disabled={isPending}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          {isPending
            ? <ActivityIndicator size="small" color="#fff" />
            : <Ionicons name="person-add" size={18} color="#fff" />
          }
        </TouchableOpacity>
      </TouchableOpacity>
    );
  };

  const renderRequestCard = (req: FriendRequest, isSent: boolean) => {
    const isPending = pendingActions.has(req.id);
    const user = isSent ? req.receiver : req.requester;
    
    return (
      <View key={req.id} style={styles.requestCard}>
        <TouchableOpacity
          style={styles.requestCardLeft}
          onPress={() => setSelectedUserId(user.id)}
          activeOpacity={0.8}
        >
          <Avatar user={user} size={56} />
          <View style={styles.requestCardInfo}>
            <Text style={styles.requestCardName} numberOfLines={1}>
              {user.prenom} {user.nom}
            </Text>
            <Text style={styles.requestCardSub}>
              {isSent
                ? t('friendsScreen.requests.sentSubtitle')
                : t('friendsScreen.requests.receivedSubtitle')}
            </Text>
            <Text style={styles.requestCardDate}>
              {new Date(req.created_at).toLocaleDateString('fr-FR', { day:'numeric', month:'short' })}
            </Text>
          </View>
        </TouchableOpacity>

        <View style={styles.requestCardActions}>
          {isSent ? (
            <TouchableOpacity
              style={[styles.actionBtn, styles.actionBtnSecondary, isPending && styles.btnDisabled]}
              onPress={() => cancelRequest(req.id)}
              disabled={isPending}
            >
              {isPending
                ? <ActivityIndicator size="small" color={COLORS.textSecondary} />
                : <Text style={styles.actionBtnSecondaryText}>{t('friendsScreen.requests.cancel')}</Text>
              }
            </TouchableOpacity>
          ) : (
            <>
              <TouchableOpacity
                style={[styles.actionBtn, styles.actionBtnPrimary, isPending && styles.btnDisabled]}
                onPress={() => acceptRequest(req.id)}
                disabled={isPending}
              >
                {isPending
                  ? <ActivityIndicator size="small" color="#fff" />
                  : <Text style={styles.actionBtnPrimaryText}>{t('friendsScreen.requests.accept')}</Text>
                }
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.actionBtn, styles.actionBtnSecondary, isPending && styles.btnDisabled]}
                onPress={() => cancelRequest(req.id)}
                disabled={isPending}
              >
                {isPending
                  ? <ActivityIndicator size="small" color={COLORS.textSecondary} />
                  : <Text style={styles.actionBtnSecondaryText}>{t('friendsScreen.requests.decline')}</Text>
                }
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    );
  };

  // ─── Tab: Suggestions ────────────────────────────────────────────────────────
  const renderSuggestions = () => (
    <ScrollView
      style={styles.tabContent}
      contentContainerStyle={styles.listContent}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.primary} />}
      onScroll={(e) => {
        // Tab bar auto-hide synchronisée
        handleScroll(e);
        // Pagination "load more" existante
        const { layoutMeasurement, contentOffset, contentSize } = e.nativeEvent;
        if (layoutMeasurement.height + contentOffset.y >= contentSize.height - 40) {
          loadMoreSuggestions();
        }
      }}
      scrollEventThrottle={16}
      onTouchEnd={handleDoubleTap}
    >
      {loading && suggestions.length === 0 ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={styles.loadingText}>{t('friendsScreen.common.loading')}</Text>
        </View>
      ) : suggestions.length === 0 ? (
        <EmptyState icon="people-outline" text={t('friendsScreen.suggestions.empty')} />
      ) : (
        <>
          <SectionHeader
            icon="sparkles"
            title={t('friendsScreen.suggestions.sectionTitle')}
            count={suggestions.length}
          />
          {suggestions.map(renderUserCard)}
          {loadingMore && (
            <View style={styles.loadMoreRow}>
              <ActivityIndicator size="small" color={COLORS.primary} />
              <Text style={styles.loadMoreText}>{t('friendsScreen.common.loading')}</Text>
            </View>
          )}
          {!hasMoreSuggestions && suggestions.length >= 20 && (
            <Text style={styles.endText}>{t('friendsScreen.suggestions.endOfList')}</Text>
          )}
        </>
      )}
    </ScrollView>
  );

  // ─── Tab: Search ─────────────────────────────────────────────────────────────
  const renderSearch = () => (
    <View style={styles.tabContent}>
      <View style={styles.searchWrap}>
        <View style={styles.searchBox}>
          <Ionicons name="search" size={18} color={COLORS.textMuted} />
          <TextInput
            style={styles.searchInput}
            placeholder={t('friendsScreen.search.placeholder')}
            placeholderTextColor={COLORS.textMuted}
            value={searchQuery}
            onChangeText={setSearchQuery}
            onSubmitEditing={searchUsers}
            returnKeyType="search"
            autoCorrect={false}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => { setSearchQuery(''); setSearchResults([]); }}>
              <Ionicons name="close-circle" size={18} color={COLORS.textMuted} />
            </TouchableOpacity>
          )}
        </View>
        <TouchableOpacity style={styles.searchBtn} onPress={searchUsers}>
          {searching
            ? <ActivityIndicator size="small" color="#fff" />
            : <Ionicons name="arrow-forward" size={18} color="#fff" />
          }
        </TouchableOpacity>
      </View>

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={styles.listContent}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onTouchEnd={handleDoubleTap}
      >
        {searching ? (
          <View style={styles.centered}>
            <ActivityIndicator size="large" color={COLORS.primary} />
          </View>
        ) : searchResults.length === 0 && searchQuery.length > 0 ? (
          <EmptyState icon="search-outline" text={t('friendsScreen.search.noResults', { query: searchQuery })} />
        ) : searchResults.length > 0 ? (
          <>
            <SectionHeader icon="search" title={t('friendsScreen.search.sectionTitle')} count={searchResults.length} />
            {searchResults.map(renderUserCard)}
          </>
        ) : (
          <View style={styles.centered}>
            <Ionicons name="search-circle-outline" size={72} color={COLORS.border} />
            <Text style={styles.searchHint}>{t('friendsScreen.search.hint')}</Text>
          </View>
        )}
      </ScrollView>
    </View>
  );

  // ─── Tab: Requests ───────────────────────────────────────────────────────────
  const renderRequests = () => (
    <View style={styles.tabContent}>
      {/* Sub-Tabs for Requests */}
      <View style={styles.segmentContainer}>
        <TouchableOpacity 
          style={[styles.segmentTab, requestTab === 'received' && styles.segmentTabActive]}
          onPress={() => { haptic('light'); setRequestTab('received'); }}
        >
          <Text style={[styles.segmentText, requestTab === 'received' && styles.segmentTextActive]}>
            {t('friendsScreen.requests.receivedTab', { count: receivedRequests.length })}
          </Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={[styles.segmentTab, requestTab === 'sent' && styles.segmentTabActive]}
          onPress={() => { haptic('light'); setRequestTab('sent'); }}
        >
          <Text style={[styles.segmentText, requestTab === 'sent' && styles.segmentTextActive]}>
            {t('friendsScreen.requests.sentTab', { count: sentRequests.length })}
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        style={styles.tabContent}
        contentContainerStyle={styles.listContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.primary} />}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        onTouchEnd={handleDoubleTap}
      >
        {loading ? (
          <View style={styles.centered}>
            <ActivityIndicator size="large" color={COLORS.primary} />
          </View>
        ) : (
          <>
            {requestTab === 'received' && (
              receivedRequests.length === 0
                ? <EmptyState icon="mail-open-outline" text={t('friendsScreen.requests.emptyReceived')} />
                : receivedRequests.map(r => renderRequestCard(r, false))
            )}

            {requestTab === 'sent' && (
              sentRequests.length === 0
                ? <EmptyState icon="paper-plane-outline" text={t('friendsScreen.requests.emptySent')} />
                : sentRequests.map(r => renderRequestCard(r, true))
            )}
          </>
        )}
      </ScrollView>
    </View>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor={COLORS.bg} />

      {/* Main Tab Bar — remonte directement en haut, header supprimé */}
      <View style={styles.tabBar}>
        {TABS_META.map(tab => {
          const isActive = activeTab === tab.key;
          const showBadge = tab.key === 'requests' && totalRequests > 0;
          return (
            <TouchableOpacity
              key={tab.key}
              style={styles.tabItem}
              onPress={() => { haptic('light'); setActiveTab(tab.key); }}
              activeOpacity={0.7}
            >
              <View style={[styles.tabIconWrap, isActive && styles.tabIconActive]}>
                <Ionicons
                  name={isActive ? tab.icon : `${tab.icon}-outline` as any}
                  size={20}
                  color={isActive ? COLORS.primary : COLORS.textMuted}
                />
                {showBadge && (
                  <View style={styles.badge}>
                    <Text style={styles.badgeText}>
                      {totalRequests > 9 ? '9+' : totalRequests}
                    </Text>
                  </View>
                )}
              </View>
              <Text style={[styles.tabLabel, isActive && styles.tabLabelActive]}>
                {t(`friendsScreen.tabs.${tab.key}`)}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Content */}
      {activeTab === 'suggestions' && renderSuggestions()}
      {activeTab === 'search'      && renderSearch()}
      {activeTab === 'requests'    && renderRequests()}

      {/* Profile Modal */}
      <VueProfil
        visible={selectedUserId !== null}
        userId={selectedUserId || ''}
        onClose={() => setSelectedUserId(null)}
      />
    </View>
  );
}

// ─── Helper components ────────────────────────────────────────────────────────
function SectionHeader({ icon, title, count }: { icon: any; title: string; count: number }) {
  return (
    <View style={shStyles.row}>
      <View style={shStyles.iconWrap}>
        <Ionicons name={icon} size={14} color={COLORS.primary} />
      </View>
      <Text style={shStyles.title}>{title}</Text>
      <View style={shStyles.countWrap}>
        <Text style={shStyles.count}>{count}</Text>
      </View>
    </View>
  );
}

const shStyles = StyleSheet.create({
  row:      { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10, gap: 8 },
  iconWrap: { width: 24, height: 24, borderRadius: 6, backgroundColor: COLORS.primary + '18', justifyContent: 'center', alignItems: 'center' },
  title:    { flex: 1, fontSize: 13, fontWeight: '700', color: COLORS.textSecondary, letterSpacing: 0.3 },
  countWrap:{ backgroundColor: COLORS.primary + '15', borderRadius: 10, paddingHorizontal: 8, paddingVertical: 2 },
  count:    { fontSize: 11, fontWeight: '700', color: COLORS.primary },
});

function EmptyState({ icon, text, compact = false }: { icon: any; text: string; compact?: boolean }) {
  return (
    <View style={[esStyles.wrap, compact && esStyles.compact]}>
      <View style={esStyles.iconBg}>
        <Ionicons name={icon} size={compact ? 32 : 44} color={COLORS.textMuted} />
      </View>
      <Text style={esStyles.text}>{text}</Text>
    </View>
  );
}

const esStyles = StyleSheet.create({
  wrap:    { alignItems: 'center', paddingVertical: 64, gap: 16 },
  compact: { paddingVertical: 32 },
  iconBg:  { width: 88, height: 88, borderRadius: 44, backgroundColor: COLORS.border + '33', justifyContent: 'center', alignItems: 'center' },
  text:    { fontSize: 15, color: COLORS.textMuted, textAlign: 'center', maxWidth: 240, lineHeight: 22, fontWeight: '500' },
});

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.bg,
  },

  // Tab bar — header supprimé (PATCH V3), padding divisé par 3 (54→18 /
  // 40→13) pour réduire au maximum la hauteur au-dessus des onglets.
  tabBar: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingTop: Platform.OS === 'ios' ? 18 : 13,
    paddingBottom: Platform.OS === 'ios' ? 8 : 0,
    elevation: 4,
    shadowColor: COLORS.primary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
  },
  tabItem: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 4,
    gap: 2,
  },
  tabIconWrap: {
    position: 'relative',
    width: 36,
    height: 24,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 10,
  },
  tabIconActive: {
    backgroundColor: COLORS.primary + '15',
  },
  tabLabel: {
    fontSize: 11,
    color: COLORS.textMuted,
    fontWeight: '600',
    letterSpacing: 0.2,
  },
  tabLabelActive: {
    color: COLORS.primary,
    fontWeight: '700',
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    backgroundColor: COLORS.badge,
    borderRadius: 9,
    minWidth: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#fff',
  },
  badgeText: {
    color: '#fff',
    fontSize: 9,
    fontWeight: '800',
  },

  // Content
  tabContent: {
    flex: 1,
  },
  listContent: {
    paddingBottom: 100,
    paddingTop: 8,
  },

  // Cards (Suggestions & Search)
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.card,
    marginHorizontal: 16,
    marginVertical: 6,
    borderRadius: 16,
    padding: 12,
    elevation: 2,
    shadowColor: COLORS.primaryDark,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.8)',
  },
  cardLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarWrap: {
    position: 'relative',
  },
  cardInfo: {
    marginLeft: 14,
    flex: 1,
  },
  cardName: {
    fontSize: 15,
    fontWeight: '700',
    color: COLORS.textPrimary,
    letterSpacing: 0.2,
  },

  // Request Cards (New Professional Design)
  requestCard: {
    backgroundColor: COLORS.card,
    marginHorizontal: 16,
    marginVertical: 8,
    borderRadius: 20,
    padding: 16,
    elevation: 3,
    shadowColor: COLORS.primaryDark,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.06,
    shadowRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.6)',
  },
  requestCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  requestCardInfo: {
    marginLeft: 14,
    flex: 1,
  },
  requestCardName: {
    fontSize: 16,
    fontWeight: '700',
    color: COLORS.textPrimary,
    letterSpacing: 0.1,
    marginBottom: 2,
  },
  requestCardSub: {
    fontSize: 13,
    color: COLORS.textSecondary,
    fontWeight: '500',
  },
  requestCardDate: {
    fontSize: 11,
    color: COLORS.textMuted,
    marginTop: 4,
    fontWeight: '500',
  },
  requestCardActions: {
    flexDirection: 'row',
    gap: 12,
  },
  actionBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  actionBtnPrimary: {
    backgroundColor: COLORS.primary,
  },
  actionBtnSecondary: {
    backgroundColor: COLORS.border + '44',
  },
  actionBtnPrimaryText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  actionBtnSecondaryText: {
    color: COLORS.textSecondary,
    fontSize: 14,
    fontWeight: '700',
  },

  // Buttons
  iconBtn: {
    width: 40,
    height: 40,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  btnPrimary: {
    backgroundColor: COLORS.primary,
  },
  btnDisabled: {
    opacity: 0.5,
  },

  // Search
  searchWrap: {
    flexDirection: 'row',
    gap: 12,
    margin: 16,
    marginBottom: 8,
  },
  searchBox: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: '#fff',
    borderRadius: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: COLORS.border,
    elevation: 2,
    shadowColor: COLORS.primary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 6,
  },
  searchInput: {
    flex: 1,
    fontSize: 15,
    color: COLORS.textPrimary,
    fontWeight: '500',
  },
  searchBtn: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: COLORS.primary,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 3,
    shadowColor: COLORS.primary,
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
  },
  searchHint: {
    fontSize: 14,
    color: COLORS.textMuted,
    marginTop: 16,
    textAlign: 'center',
    fontWeight: '500',
  },

  // Sub-Tabs Segment Control (Requests)
  segmentContainer: {
    flexDirection: 'row',
    backgroundColor: COLORS.border + '40',
    borderRadius: 14,
    padding: 4,
    marginHorizontal: 16,
    marginTop: 16,
    marginBottom: 4,
  },
  segmentTab: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 10,
  },
  segmentTabActive: {
    backgroundColor: '#fff',
    shadowColor: COLORS.primaryDark,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 2,
  },
  segmentText: {
    fontSize: 13,
    fontWeight: '600',
    color: COLORS.textMuted,
  },
  segmentTextActive: {
    color: COLORS.primary,
    fontWeight: '700',
  },

  // Misc
  centered: {
    alignItems: 'center',
    paddingVertical: 80,
    gap: 16,
  },
  loadingText: {
    fontSize: 14,
    color: COLORS.textMuted,
    fontWeight: '500',
  },
  loadMoreRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 20,
    gap: 10,
  },
  loadMoreText: {
    fontSize: 13,
    color: COLORS.textMuted,
    fontWeight: '600',
  },
  endText: {
    textAlign: 'center',
    color: COLORS.textMuted,
    fontSize: 13,
    paddingVertical: 24,
    fontWeight: '500',
  },
});
